import { z } from 'zod';

/** Client creative prefix when nobody has saved a system setting. */
export const DEFAULT_CLIENT_PATH = 'https://storage.googleapis.com/adgepublic';

/** Competitor creative prefix when nobody has saved a system setting. */
export const DEFAULT_COMPETITORS_PATH = 'https://storage.googleapis.com/adgepublic/pub_data';

/** Previous name for the client default. Creative URLs read `clientPath`. */
export const DEFAULT_ASSETS_BASE_URL = DEFAULT_CLIENT_PATH;

export const CLIENT_PATH_TOKEN = '{client}';
export const COMPETITOR_PATH_TOKEN = '{competitor}';

/** Shown by the admin page and the API when a path is not an https template. */
export const CREATIVE_PATH_MESSAGE = 'Path must be an https URL without a trailing slash';

/** Long enough for a host, a bucket path, and one `{client}` or `{competitor}` token. */
export const CREATIVE_PATH_MAX_LENGTH = 512;

const CREATIVE_PATH_TOKENS = [CLIENT_PATH_TOKEN, COMPETITOR_PATH_TOKEN] as const;

type CreativePathToken = (typeof CREATIVE_PATH_TOKENS)[number];

function creativePathSchema(allowedToken: CreativePathToken) {
  return z
    .string()
    .trim()
    .max(CREATIVE_PATH_MAX_LENGTH, CREATIVE_PATH_MESSAGE)
    .superRefine((value, ctx) => {
      const reject = () => {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: CREATIVE_PATH_MESSAGE });
      };
      if (value.length === 0 || value.endsWith('/')) {
        reject();
        return;
      }
      // A space, query, or fragment would sit in front of every creative file.
      // `new URL` drops a bare trailing `?` or `#`, so check the raw string.
      if (/\s/.test(value) || value.includes('?') || value.includes('#')) {
        reject();
        return;
      }
      const allowedCount = value.split(allowedToken).length - 1;
      if (allowedCount > 1) {
        reject();
        return;
      }
      // Any `{…}` other than this field's own token, including `{foo}` and `{CLIENT}`.
      const withoutAllowed = value.split(allowedToken).join('');
      if (withoutAllowed.includes('{') || withoutAllowed.includes('}')) {
        reject();
        return;
      }
      const probe = value.replaceAll(allowedToken, 'name');
      const afterScheme = probe.split('://');
      // `https://` is the only double slash. `https://host//bucket` is an empty segment.
      if (afterScheme.length !== 2 || afterScheme[1]?.includes('//')) {
        reject();
        return;
      }
      if (/(^|\/)\.\.?(\/|$)/.test(probe)) {
        reject();
        return;
      }
      let parsed: URL;
      try {
        parsed = new URL(probe);
      } catch {
        reject();
        return;
      }
      const segments = parsed.pathname.split('/').filter((segment) => segment.length > 0);
      if (
        parsed.protocol !== 'https:' ||
        parsed.username.length > 0 ||
        parsed.password.length > 0 ||
        parsed.search.length > 0 ||
        parsed.hash.length > 0 ||
        segments.some((segment) => segment === '.' || segment === '..')
      ) {
        reject();
      }
    });
}

/** Client prefix. `{client}` is replaced; otherwise the client name is appended. */
export const ClientPathSchema = creativePathSchema(CLIENT_PATH_TOKEN);

/** Competitor prefix. `{competitor}` is replaced; otherwise the competitor name is appended. */
export const CompetitorsPathSchema = creativePathSchema(COMPETITOR_PATH_TOKEN);

/**
 * Folder for one client or competitor.
 * `https://storage.googleapis.com/adgepublic` + `acme` → `.../adgepublic/acme`.
 * `https://cdn.adge.ai/{client}` + `acme` → `https://cdn.adge.ai/acme`.
 */
export function resolveCreativePath(template: string, token: CreativePathToken, name: string): string {
  if (template.includes(token)) return template.replaceAll(token, name);
  return `${template}/${name}`;
}

const ADGEPUBLIC_HOST = 'storage.googleapis.com';
const ADGEPUBLIC_BUCKET = 'adgepublic';
const PUB_DATA_SEGMENT = 'pub_data';
const PROFILE_PIC_SUFFIX = '_profile_pic.png';

export interface CreativePathPair {
  clientPath: string;
  competitorsPath: string;
}

/**
 * `{path}/{brand}/{brand}_profile_pic.png`. A grouped name (`Multiple` or a
 * comma list) has no single icon.
 */
export function brandIconUrl(
  template: string,
  token: CreativePathToken,
  brand: string | null | undefined,
): string | null {
  const name = brand?.trim() ?? '';
  if (name.length === 0 || name === 'Multiple' || name.includes(',')) return null;
  return `${resolveCreativePath(template, token, name)}/${name}${PROFILE_PIC_SUFFIX}`;
}

/** Client creatives use `{client}`; every other source uses `{competitor}`. */
export function creativeBrandIconUrl(
  source: string,
  brand: string | null | undefined,
  paths: CreativePathPair,
): string | null {
  if (source === 'client') return brandIconUrl(paths.clientPath, CLIENT_PATH_TOKEN, brand);
  return brandIconUrl(paths.competitorsPath, COMPETITOR_PATH_TOKEN, brand);
}

/**
 * A stored `https://storage.googleapis.com/adgepublic/...` asset is rebuilt
 * from the path inputs. `pub_data/{brand}/...` uses the competitors path;
 * `{brand}/...` uses the client path. Other hosts are unchanged, and the
 * shipping defaults reproduce the original URL.
 */
export function relocateAdgepublicAssetUrl(
  url: string | null | undefined,
  paths: CreativePathPair,
): string | null {
  const raw = url?.trim() ?? '';
  if (raw.length === 0) return null;
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return raw;
  }
  if (parsed.protocol !== 'https:' || parsed.hostname !== ADGEPUBLIC_HOST) return raw;
  const segments = parsed.pathname.split('/').filter((segment) => segment.length > 0);
  if (segments[0] !== ADGEPUBLIC_BUCKET) return raw;
  const afterBucket = segments.slice(1);
  const isCompetitor = afterBucket[0] === PUB_DATA_SEGMENT;
  const brandIndex = isCompetitor ? 1 : 0;
  const brand = afterBucket[brandIndex];
  const rest = afterBucket.slice(brandIndex + 1);
  if (!brand || rest.length === 0) return raw;
  const folder = resolveCreativePath(
    isCompetitor ? paths.competitorsPath : paths.clientPath,
    isCompetitor ? COMPETITOR_PATH_TOKEN : CLIENT_PATH_TOKEN,
    decodeURIComponent(brand),
  );
  return `${folder}/${rest.join('/')}${parsed.search}${parsed.hash}`;
}

/**
 * BigQuery expression for the client folder. `nameSql` is a bound parameter
 * (`@userName`) or a column (`client`), never a concatenated value.
 */
export function clientPathFolderSql(pathParam: string, nameSql: string): string {
  return `IF(STRPOS(${pathParam}, '${CLIENT_PATH_TOKEN}') > 0, REPLACE(${pathParam}, '${CLIENT_PATH_TOKEN}', ${nameSql}), CONCAT(${pathParam}, '/', ${nameSql}))`;
}
